import React, { useState } from 'react';
import {
  ChevronUp,
  ChevronDown,
  CheckCircle,
  Award,
  Code
} from 'lucide-react';
import ScratchStemEditor from './ScratchStemEditor';
import ScratchReferenceAnswer from './ScratchReferenceAnswer';
import ScratchScoringRules from './ScratchScoringRules';
import Editor from '@monaco-editor/react';

interface Question {
  id: string;
  type: string;
  title: string;
  score: number;
  content?: string;
  referenceAnswer?: string;
  scoringPoints?: string;
  codeTemplate?: string;
  titleImageWidths?: number[];
  referenceAnswerImageWidths?: number[];
  scoringPointsImageWidths?: number[];
  referenceSb3Url?: string;
  [key: string]: any;
}

interface CppQuestionEditorProps {
  question: Question;
  editSessionId: number;
  onUpdateQuestion: (id: string, data: Partial<Question>) => void;
}

export default function CppQuestionEditor({
  question,
  editSessionId,
  onUpdateQuestion,
}: CppQuestionEditorProps) {
  // 折叠状态管理（仅 UI 状态，与数据无关）
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    stem: true,
    answer: true,
    reference: true,
    rules: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="space-y-4">
      {/* 1. 题干区 - 直接渲染编辑器，参照客观题极简风格 */}
      <ScratchStemEditor
        key={`stem-${question.id}-${editSessionId}`}
        value={question.content || ''}
        onChange={(content, widths) => {
          onUpdateQuestion(question.id, { content });
          if (widths && widths.length > 0) {
            onUpdateQuestion(question.id, { titleImageWidths: widths });
          }
        }}
        questionId={question.id}
        imageWidths={question.titleImageWidths || []}
      />

      {/* 2. 作答设置区 - C++专属 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('answer')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium text-gray-900">作答配置</span>
          </div>
          {expandedSections.answer ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.answer && (
          <div className="p-4 space-y-4">
            {/* 蓝色提示框 */}
            <div className="flex items-start gap-2 px-4 py-3 bg-blue-50 rounded-lg border border-blue-100">
              <Code className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <span className="text-sm text-blue-700">
                学生需在代码编辑器中编写 C++ 代码，系统将自动编译并判分
              </span>
            </div>

            {/* 代码模板（可选） */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                代码模板（可选）
              </label>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Editor
                  height="200px"
                  language="cpp"
                  theme="vs"
                  value={question.codeTemplate || ''}
                  onChange={(value) => onUpdateQuestion(question.id, { codeTemplate: value || '' })}
                  options={{
                    fontSize: 14,
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    insertSpaces: true,
                    lineNumbers: 'on',
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. 参考答案区 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('reference')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-600" />
            <span className="font-medium text-gray-900">参考答案</span>
          </div>
          {expandedSections.reference ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.reference && (
          <div className="p-4">
            <ScratchReferenceAnswer
              key={`ref-answer-${question.id}-${editSessionId}`}
              programReferenceUrl={question.referenceSb3Url}
              onProgramReferenceChange={(url) => onUpdateQuestion(question.id, { referenceSb3Url: url })}
              answerText={question.referenceAnswer || ''}
              onAnswerTextChange={(text, widths) => {
                onUpdateQuestion(question.id, { referenceAnswer: text });
                if (widths && widths.length > 0) {
                  onUpdateQuestion(question.id, { referenceAnswerImageWidths: widths });
                }
              }}
              questionId={question.id}
              answerImageWidths={question.referenceAnswerImageWidths || []}
              showProgramDownload={false}
            />
          </div>
        )}
      </div>

      {/* 4. 评分规则区 */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection('rules')}
          className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" />
            <span className="font-medium text-gray-900">评分规则</span>
          </div>
          {expandedSections.rules ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {expandedSections.rules && (
          <div className="p-4">
            <ScratchScoringRules
              key={`scoring-${question.id}-${editSessionId}`}
              value={question.scoringPoints || ''}
              onChange={(text, widths) => {
                onUpdateQuestion(question.id, { scoringPoints: text });
                if (widths && widths.length > 0) {
                  onUpdateQuestion(question.id, { scoringPointsImageWidths: widths });
                }
              }}
              questionId={question.id}
              imageWidths={question.scoringPointsImageWidths || []}
            />
          </div>
        )}
      </div>
    </div>
  );
}
